import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Check, Package, Plus, Sparkles, X } from "lucide-react";

import {
  useAddIngestionPhoto,
  useAnalyseIngestion,
  useChooseIngestionProduct,
  useConfirmIngestion,
  useIngestionSession,
  useSetIngestionDestination,
  useSetIngestionFields,
  useSetIngestionQuantity,
  useStartIngestion,
} from "@/api/queries";
import type { IngestionCandidate, IngestionSession, Uuid } from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Field, Input } from "@/components/Input";
import { ErrorState, LoadingState } from "@/components/States";
import { Photo } from "@/components/Photo";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { reportMutationError } from "@/lib/mutation-errors";
import { PageHeader } from "@/layout/PageHeader";
import { Page } from "@/layout/Page";
import { ConfidenceIndicator, SourceTag } from "./ConfidenceIndicator";
import { DegradedNotice } from "./DegradedNotice";
import { FieldProvenance } from "./FieldProvenance";
import { PhysicalProposals } from "./PhysicalProposals";
import { PlacePicker } from "./PlacePicker";
import { QuantityControl } from "./QuantityControl";

/** The fields an operator fills in, in the order they think about them. */
interface FieldSpec {
  name: string;
  label: string;
  required?: boolean;
}

const FIELDS: FieldSpec[] = [
  { name: "name", label: "Nom", required: true },
  { name: "manufacturer", label: "Fabricant" },
  { name: "model", label: "Modèle" },
  { name: "part_number", label: "Numéro de pièce (MPN)" },
  { name: "sku", label: "SKU interne" },
];

/**
 * Smart Inventory Input (TICKETS FE-011, FE-012, FE-013).
 *
 * Photo, scan or straight typing — all three land in the same draft, and the
 * draft writes nothing until it is confirmed. The AI fills fields in; it never
 * takes the decision, and every proposed value shows where it came from.
 */
export function SmartInputPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [sessionId, setSessionId] = useState<Uuid | null>(params.get("session"));
  const start = useStartIngestion();
  const started = useRef(false);

  useEffect(() => {
    if (sessionId || started.current) return;
    started.current = true;
    start.mutate(
      { mode: "MANUAL" },
      {
        onSuccess: (session) => setSessionId(session.id),
        onError: () => toast.error("Impossible de démarrer la saisie"),
      },
    );
  }, [sessionId, start, toast]);

  if (!sessionId) return <LoadingState label="Préparation de la saisie…" />;

  return (
    <SmartInputSession
      sessionId={sessionId}
      onDone={(productId) => navigate(productId ? `/products/${productId}` : "/inventory")}
    />
  );
}

function SmartInputSession({
  sessionId,
  onDone,
}: {
  sessionId: Uuid;
  onDone: (productId: Uuid | null) => void;
}) {
  const toast = useToast();
  const { data: session, isLoading, error, refetch } = useIngestionSession(sessionId);

  const addPhoto = useAddIngestionPhoto();
  const analyse = useAnalyseIngestion();
  // Not an editable field: `certification` is not a column on a product. It is
  // an observation the pipeline moved out of `part_number`, and the operator is
  // owed the sight of it.
  const certification = session?.proposals.find((p) => p.field_name === "certification");
  const setFields = useSetIngestionFields();
  const chooseProduct = useChooseIngestionProduct();
  const setQuantity = useSetIngestionQuantity();
  const setDestination = useSetIngestionDestination();
  const confirm = useConfirmIngestion();

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [quantity, setQuantityValue] = useState("1");
  const fileInput = useRef<HTMLInputElement>(null);
  // One key per session: a retried confirm must not stock the shelf twice.
  const idempotencyKey = useRef(`smart-input-${sessionId}`);

  useEffect(() => {
    if (session) setDraft((current) => ({ ...session.values, ...current }));
  }, [session]);

  if (isLoading) return <LoadingState label="Chargement de la saisie…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!session) return null;

  const busy =
    addPhoto.isPending ||
    analyse.isPending ||
    setFields.isPending ||
    confirm.isPending;

  const chosenProduct = session.resolved_product_id;
  const canConfirm =
    Boolean(chosenProduct || (session.creates_new_product && draft.name?.trim())) &&
    Number.parseFloat(quantity) > 0 &&
    Boolean(session.target_location_id || session.target_container_id);

  async function handleConfirm() {
    await setFields.mutateAsync({ sessionId, values: draft });
    await setQuantity.mutateAsync({ sessionId, quantity });
    const result = await confirm.mutateAsync({
      sessionId,
      idempotencyKey: idempotencyKey.current,
    });
    toast.success(
      "Article ajouté",
      result.transaction_id ? "Le mouvement est inscrit au ledger." : undefined,
    );
    onDone(result.product_id);
  }

  return (
    <Page>
      <PageHeader
        title="Ajouter un article"
        subtitle="Photo, code-barres ou saisie directe. Rien n'est enregistré avant votre confirmation."
        actions={
          <Button variant="ghost" onClick={() => onDone(null)}>
            <X className="size-4" aria-hidden />
            Annuler
          </Button>
        }
      />

      <div className="grid min-w-0 gap-3 @4xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-3">
          <Card>
            <CardHeader
              title="Photo"
              subtitle="Facultatif — la saisie manuelle fonctionne toujours."
            />
            <CardBody className="flex flex-col gap-3">
              {session.media.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {session.media.map((item) => (
                    <Photo
                      key={item.id}
                      mediaId={item.media_id}
                      alt="Photo de la saisie"
                      className="aspect-square w-full"
                    />
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-ink-subtle">
                  Aucune photo. Vous pouvez tout saisir à la main.
                </p>
              )}

              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  addPhoto.mutate(
                    { sessionId, file },
                    { onError: () => toast.error("Photo non envoyée") },
                  );
                  event.target.value = "";
                }}
              />

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  onClick={() => fileInput.current?.click()}
                  loading={addPhoto.isPending}
                >
                  <Camera className="size-4" aria-hidden />
                  Prendre une photo
                </Button>
                <Button
                  variant="primary"
                  disabled={session.media.length === 0}
                  loading={analyse.isPending}
                  onClick={() =>
                    analyse.mutate(
                      { sessionId },
                      { onError: () => toast.error("Analyse impossible") },
                    )
                  }
                >
                  <Sparkles className="size-4" aria-hidden />
                  Analyser
                </Button>
              </div>

              {session.status !== "DRAFT" && (
                <div className="flex flex-col gap-2 border-t border-border pt-3">
                  <ConfidenceIndicator level={session.confidence} showHint />
                  {/* A missing model is not an analysis failure: what was read
                      is on screen and usable. The panel says so and offers the
                      two moves that make sense, rather than an error. */}
                  <DegradedNotice
                    session={session}
                    retrying={analyse.isPending}
                    onRetryVision={() =>
                      analyse.mutate(
                        { sessionId, retryVision: true },
                        { onError: () => toast.error("Analyse impossible") },
                      )
                    }
                  />
                  {session.failure_reason && (
                    <p className="rounded-md bg-warning-subtle px-3 py-2 text-[12px] text-warning">
                      {session.failure_reason}
                    </p>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          <CandidateList
            session={session}
            onChoose={(productId) =>
              chooseProduct.mutate({ sessionId, product_id: productId })
            }
            onCreateNew={() => chooseProduct.mutate({ sessionId, create_new: true })}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <Card>
            <CardHeader
              title="Détails de l'article"
              subtitle="Corrigez librement — ce que la machine a proposé est conservé."
            />
            <CardBody className="flex flex-col gap-3">
              {FIELDS.map((entry) => {
                const proposal = session.proposals.find((p) => p.field_name === entry.name);
                return (
                  <Field
                    key={entry.name}
                    label={entry.label}
                    htmlFor={`field-${entry.name}`}
                    hint={
                      proposal?.was_corrected
                        ? `Proposé : ${proposal.original_value} — corrigé par vous`
                        : undefined
                    }
                  >
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <Input
                          id={`field-${entry.name}`}
                          value={draft[entry.name] ?? ""}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              [entry.name]: event.target.value,
                            }))
                          }
                          placeholder={entry.required ? "Obligatoire" : ""}
                          aria-invalid={proposal?.contested ? true : undefined}
                        />
                        {proposal && (
                          <SourceTag source={proposal.source} provider={proposal.provider} />
                        )}
                      </div>
                      {proposal && (
                        <FieldProvenance
                          proposal={proposal}
                          onChoose={(value) =>
                            setDraft((current) => ({ ...current, [entry.name]: value }))
                          }
                        />
                      )}
                    </div>
                  </Field>
                );
              })}

              <PhysicalProposals
                session={session}
                draft={draft}
                onChange={(name, value) =>
                  setDraft((current) => ({ ...current, [name]: value }))
                }
              />

              {/* Read-only. A certification file number — `CSA LR3838`,
                  `UL E12345` — is a fact about the product, so it is kept and
                  shown; it is simply not the part number, which is what the
                  semantic layer upstream just decided. Showing it is how the
                  operator sees that the reclassification happened instead of
                  wondering where the value went. */}
              {certification && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
                  <span className="text-[12px] text-ink-muted">Certification lue</span>
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-[12px] text-ink">
                      {certification.accepted_value ?? certification.original_value}
                    </span>
                    <SourceTag
                      source={certification.source}
                      provider={certification.provider}
                    />
                  </span>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Quantité et emplacement" />
            <CardBody className="flex flex-col gap-4">
              <div>
                <p className="mb-2 text-[13px] font-medium text-ink-muted">Quantité</p>
                <QuantityControl value={quantity} onChange={setQuantityValue} disabled={busy} />
              </div>
              <PlacePicker
                locationId={session.target_location_id}
                containerId={session.target_container_id}
                onChange={(place) => setDestination.mutate({ sessionId, ...place })}
              />
            </CardBody>
          </Card>

          <Button
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canConfirm}
            loading={confirm.isPending}
            onClick={() => {
              void handleConfirm().catch((caught) =>
                reportMutationError(toast, "Confirmation impossible", caught),
              );
            }}
          >
            <Check className="size-5" aria-hidden />
            Confirmer et ajouter
          </Button>
          {!canConfirm && (
            <p className="text-center text-[12px] text-ink-subtle">
              Il faut un produit, une quantité et un emplacement.
            </p>
          )}
        </div>
      </div>
    </Page>
  );
}

/**
 * Candidates, side by side (TICKET FE-012).
 *
 * When two products are plausible they are shown next to each other with what
 * matched, because "deux correspondances possibles" is a question for a person —
 * hiding it behind a score would be the system pretending to know.
 */
function CandidateList({
  session,
  onChoose,
  onCreateNew,
}: {
  session: IngestionSession;
  onChoose: (productId: Uuid) => void;
  onCreateNew: () => void;
}) {
  const ambiguous = session.candidates.length > 1;
  // One product, matched deterministically. Worth naming out loud (§10): the
  // operator must never be pushed towards creating a second catalogue entry for
  // something HD-Vault already knows — that is how a catalogue grows two
  // Milwaukee 2904-20 and nobody notices.
  const only = session.candidates.length === 1 ? session.candidates[0] : undefined;
  const known = !ambiguous && only?.is_exact ? only : null;

  return (
    <Card>
      <CardHeader
        title={
          ambiguous
            ? "Plusieurs correspondances possibles"
            : known
              ? "Produit existant trouvé"
              : "Produit"
        }
        subtitle={
          ambiguous
            ? "Choisissez laquelle est la bonne — HD-Vault ne décide pas à votre place."
            : known
              ? "Ajoutez la quantité à cette fiche plutôt que d'en créer une deuxième."
              : undefined
        }
      />
      <CardBody className="flex flex-col gap-2">
        {session.candidates.map((candidate) => (
          <CandidateRow
            key={candidate.product_id}
            candidate={candidate}
            selected={session.resolved_product_id === candidate.product_id}
            onChoose={() => onChoose(candidate.product_id)}
          />
        ))}

        {known && (
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/products/${known.product_id}`}
              className="flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-[13px] text-ink-muted hover:border-border-strong hover:text-ink"
            >
              <Package className="size-3.5" aria-hidden />
              Voir le produit
            </Link>
            <button
              type="button"
              onClick={onCreateNew}
              className="flex h-9 items-center gap-1.5 rounded-md px-3 text-[13px] text-ink-subtle hover:text-ink"
            >
              Ce n'est pas le bon
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onCreateNew}
          className={
            session.creates_new_product
              ? "flex items-center gap-3 rounded-md border border-brand bg-brand-subtle px-3 py-2.5 text-left"
              : "flex items-center gap-3 rounded-md border border-dashed border-border px-3 py-2.5 text-left hover:border-border-strong hover:bg-surface-hover"
          }
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-surface-raised text-ink-subtle">
            <Plus className="size-5" aria-hidden />
          </span>
          <span className="min-w-0">
            <span className="block text-[13px] text-ink">Créer un nouveau produit</span>
            <span className="block text-[11px] text-ink-subtle">
              Un exemplaire de plus d'un produit existant est un asset, pas un nouveau produit.
            </span>
          </span>
          {session.creates_new_product && (
            <Check className="ml-auto size-4 shrink-0 text-brand" aria-hidden />
          )}
        </button>
      </CardBody>
    </Card>
  );
}

function CandidateRow({
  candidate,
  selected,
  onChoose,
}: {
  candidate: IngestionCandidate;
  selected: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      className={
        selected
          ? "flex items-center gap-3 rounded-md border border-brand bg-brand-subtle px-3 py-2.5 text-left"
          : "flex items-center gap-3 rounded-md border border-border px-3 py-2.5 text-left hover:border-border-strong hover:bg-surface-hover"
      }
    >
      {candidate.media_id ? (
        <Photo
          mediaId={candidate.media_id}
          alt={candidate.product_name}
          className="size-10 shrink-0"
        />
      ) : (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface-sunken text-ink-subtle">
          <Package className="size-5" aria-hidden />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-ink">{candidate.product_name}</span>
        <span className="block truncate text-[11px] text-ink-subtle">
          {[candidate.product_manufacturer, candidate.product_part_number]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <span className="mt-0.5 block truncate text-[10px] text-ink-subtle">
          correspond sur : {candidate.matched_on}
        </span>
      </span>
      {candidate.is_exact && <StatusBadge tone="available">Exact</StatusBadge>}
      {selected && <Check className="size-4 shrink-0 text-brand" aria-hidden />}
    </button>
  );
}
